import { apiClient } from './client';

/**
 * Organization API client (P3-ORG-FE) — backend `apps/chat-server/src/modules/organization/organization.routes.ts` 와 1:1.
 */

export interface DivisionDto {
  id: string;
  name: string;
  sortOrder: number;
  createdAt: string;
}

export interface TeamDto {
  id: string;
  /** 부서는 선택 사항 — 부서 없이 팀만 두는 조직에서는 null (migration 100). */
  divisionId: string | null;
  name: string;
  sortOrder: number;
  createdAt: string;
}

export interface TeamMemberDto {
  id: string;
  isLeader: boolean;
  joinedAt: string;
  user: UserSummary;
}

export interface UserSummary {
  id: string;
  email: string;
  name: string;
  position: string | null;
  role: string;
  status: string;
  isActive: boolean;
  /**
   * ★★ **서버가 더 이상 보내지 않는다** (2026-08-28 · 자리 재정의 판 3).
   *    이 라우트는 로그인한 모두에게 조직 트리를 주므로, 여기에 개인 휴대전화를 실으면
   *    **팀장도 못 보던 것을 아무 동료나** 보게 된다. 사람 상세는 범위를 아는 라우트가
   *    답한다 — `api/hr-people.ts` `fetchPersonCard`.
   *    타입에는 남겨 둔다: 아직 옛 서버가 보내 줄 수 있고, 받은 값을 버릴 이유는 없다.
   */
  phone?: string | null;
  employeeNumber?: string | null;
  avatarUrl?: string | null;
}

export interface TeamDetailResponse {
  // 무소속 팀은 division 이 null 이다.
  team: TeamDto & { division: { id: string; name: string } | null };
  members: TeamMemberDto[];
}

export async function listDivisions(): Promise<DivisionDto[]> {
  const res = await apiClient.get<{ divisions: DivisionDto[] }>('/organization/divisions');
  return Array.isArray(res.data?.divisions) ? res.data.divisions : [];
}

/**
 * institution 의 **모든 팀** — 부서 하위 팀 + 부서 없는 팀 (migration 100, additive).
 *
 * `listTeams(divisionId)` 는 부서를 반드시 거치므로 무소속 팀을 볼 수 없다. 조직도가
 * "부서 > 팀" 과 "팀만" 을 함께 그리려면 이 목록이 필요하다.
 */
export async function listAllTeams(): Promise<Array<TeamDto & { divisionName: string | null }>> {
  const res = await apiClient.get<{ teams: Array<TeamDto & { divisionName: string | null }> }>('/organization/teams');
  return Array.isArray(res.data?.teams) ? res.data.teams : [];
}

export async function listTeams(divisionId: string): Promise<TeamDto[]> {
  const res = await apiClient.get<{ teams: TeamDto[] }>(`/organization/divisions/${divisionId}/teams`);
  return Array.isArray(res.data?.teams) ? res.data.teams : [];
}

/**
 * 회사 직속 구성원 — 어느 팀에도 속하지 않은 사용자(대표이사 등).
 *
 * 팀 경로로만 사람을 찾으면 팀 없는 사람은 조직도에서 통째로 사라진다. 조직도가 회사 노드
 * 바로 아래에 이들을 놓기 위해 쓴다. 서버에 없는 옛 배포에서는 404 → 빈 배열로 접는다.
 */
export async function listDirectMembers(): Promise<UserSummary[]> {
  try {
    const res = await apiClient.get<{ members: UserSummary[] }>('/organization/direct-members');
    return Array.isArray(res.data?.members) ? res.data.members : [];
  } catch {
    return [];
  }
}

export async function getTeam(teamId: string): Promise<TeamDetailResponse> {
  const res = await apiClient.get<TeamDetailResponse>(`/organization/teams/${teamId}`);
  return res.data;
}
